"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Pipette as AlertTriangle, ArrowRight, Droplets, Flame, PhoneCall, Pipette, ShieldAlert, Waves, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ProblemItem {
  id: string;
  title: string;
  description: string;
  severity: string;
  icon: "droplets" | "flame" | "waves" | "shield" | "wrench" | "pipette";
}
const plumbingProblems: ProblemItem[] = [{
  id: "clogged-drains",
  title: "Severe Drain Blockages",
  description: "Standing water in sinks, tubs, or slow draining fixtures caused by grease, roots, or mineral buildup.",
  severity: "Immediate Dispatch",
  icon: "waves"
}, {
  id: "water-leaks",
  title: "Hidden & Burst Pipe Leaks",
  description: "Sudden drops in water pressure, damp drywall, or pooling water under flooring requiring electronic leak tracing.",
  severity: "Emergency Risk",
  icon: "droplets"
}, {
  id: "no-hot-water",
  title: "Water Heater Failure",
  description: "Total loss of hot water, pilot light failures, metallic odors, or leaking tank reservoirs.",
  severity: "Same-Day Fix",
  icon: "flame"
}, {
  id: "sewer-backup",
  title: "Main Sewer Line Backup",
  description: "Foul sewage smells, multiple gurgling drains, and contaminated waste rising through lower ground fixtures.",
  severity: "Hazardous Alert",
  icon: "shield"
}, {
  id: "running-toilets",
  title: "Constant Running Toilets",
  description: "Faulty flappers, fill valves, and overflowing tank mechanisms causing high utility bills and fixture wear.",
  severity: "Fast Repair",
  icon: "pipette"
}, {
  id: "low-pressure",
  title: "Sudden Pressure Loss",
  description: "Disrupted water supply across the property caused by main line faults, regulator failures, or severe blockages.",
  severity: "Priority Response",
  icon: "wrench"
}];
function ProblemIcon({
  type
}: {
  type: ProblemItem["icon"];
}) {
  switch (type) {
    case "waves":
      return <Waves className="size-6 text-primary" />;
    case "droplets":
      return <Droplets className="size-6 text-primary" />;
    case "flame":
      return <Flame className="size-6 text-primary" />;
    case "shield":
      return <ShieldAlert className="size-6 text-primary" />;
    case "pipette":
      return <AlertTriangle className="size-6 text-primary" />;
    case "wrench":
    default:
      return <Wrench className="size-6 text-primary" />;
  }
}
export default function Problems() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="common-plumbing-problems" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pb-12 border-b border-border">
          <div className="max-w-2xl space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-secondary-foreground">
              <span className="size-2 rounded-full bg-accent" />
              <span>Diagnostic Problem Directory</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              Common Plumbing Problems We Solve Fast
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground font-sans">
              Identify your urgent issue below for instant troubleshooting steps and rapid on-site dispatch in Jonesboro, GA.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild className="rounded-full bg-accent text-accent-foreground font-bold shadow-sm hover:brightness-105 h-11 px-5 transition-[filter,box-shadow]">
              <a href="tel:4708479076" className="flex items-center gap-2">
                <PhoneCall className="size-4" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-border bg-background text-foreground hover:bg-secondary hover:text-secondary-foreground h-11 px-5 font-semibold transition-colors">
              <Link to="/problems/" className="flex items-center gap-2">
                <span>Problem Hub</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Problems Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-10">
          {plumbingProblems.map((problem, idx) => <motion.div key={problem.id} data-index={idx} variants={itemVariants} className="h-full">
              <Card className="group relative flex h-full flex-col justify-between overflow-hidden rounded-xl border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow,background-color] duration-200 hover:border-primary/50 hover:bg-background hover:shadow-md">
                {/* Decorative Amber Accent Stripe on Hover */}
                <div className="absolute top-0 left-0 h-full w-1 bg-accent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />

                <div>
                  <div className="flex items-start justify-between gap-4 pb-4">
                    <div className="flex size-12 items-center justify-center rounded-lg bg-secondary text-primary transition-colors duration-200 group-hover:bg-primary/10">
                      <ProblemIcon type={problem.icon} />
                    </div>
                    <span className="rounded-full border border-border bg-background px-2.5 py-1 text-xs font-semibold text-muted-foreground">
                      {problem.severity}
                    </span>
                  </div>

                  <CardHeader className="p-0 space-y-2">
                    <CardTitle className="text-xl font-bold text-card-foreground font-default">
                      {problem.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed font-sans">
                      {problem.description}
                    </CardDescription>
                  </CardHeader>
                </div>

                <CardFooter className="p-0 pt-6 mt-4 border-t border-border flex items-center justify-between">
                  <Link to="/problems/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:text-foreground transition-colors">
                    <span>Diagnosis & Solutions</span>
                    <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>

                  <Button asChild size="sm" variant="ghost" className="h-8 rounded-full px-3 text-xs font-bold text-foreground hover:bg-secondary">
                    <a href="tel:4708479076">
                      <PhoneCall className="size-3.5 mr-1 text-accent" />
                      <span>Dispatch</span>
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Fast-Action Callout */}
        <div className="mt-12 rounded-2xl bg-secondary/60 border border-border p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg md:text-xl font-bold text-foreground font-default">
              Don&apos;t see your specific issue listed?
            </h3>
            <p className="text-sm text-muted-foreground font-sans">
              Our 24/7 Jonesboro technicians troubleshoot and repair any residential plumbing emergency immediately.
            </p>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-center">
            <Button asChild className="w-full sm:w-auto rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-10 px-6 shadow-xs">
              <Link to="/contact/">
                <span>Request Rapid Inspection</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}